import React, { useEffect, useMemo, useState } from "react";
import { getAllEvents } from "../../../services/eventServices";
import { getAllEnseignants } from "../../../services/adherentServices";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import FiltersBar from "./FiltersBar";
import EventCard from "./EventCard";

const PAGE_SIZE = 8;

const cateEvents = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
];

const SuiviCompetitions = () => {
  useScrollToTop();
  const { user } = useUser();
  const role = getRoleFromRoleId(user?.role_id);

  const [events, setEvents] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [searchEvent, setSearchEvent] = useState("");       // recherche sur titre
  const [dateFilter, setDateFilter] = useState("upcoming"); // "all" | "past" | "upcoming"
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [onlyWithRegistrations, setOnlyWithRegistrations] = useState(false);

  // Pagination côté client (on évite d’afficher 200 cartes d’un coup)
  const [page, setPage] = useState(1);

  // Petite cache pour compter les inscrits par évènement (remontée depuis chaque EventCard)
  const [countsMap, setCountsMap] = useState({}); // { [eventId]: number }

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [evs, ens] = await Promise.all([
          getAllEvents(),
          getAllEnseignants(),
        ]);
        setEvents(Array.isArray(evs) ? evs : []);
        setEnseignants(Array.isArray(ens) ? ens : []);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement des événements.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Types distincts (pour le filtre)
  const eventTypes = useMemo(() => {
    const setT = new Set((events || []).map((e) => e.type_event).filter(Boolean));
    return Array.from(setT).sort((a, b) => a.localeCompare(b, "fr"));
  }, [events]);

  // Filtres évènements (sans charger les inscrits !)
  const filteredEvents = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const q = searchEvent.trim().toLowerCase();

    return (events || []).filter((ev) => {
      // Date
      const d = new Date(ev.start);
      let okDate = true;
      if (dateFilter === "past") okDate = d < today;
      if (dateFilter === "upcoming") okDate = d >= today;

      // Catégories
      const categories = ev.cate_event ? ev.cate_event.split(",").map(s => s.trim()) : [];
      const okCate = categoryFilter ? categories.includes(categoryFilter) : true;

      // Type
      const okType = typeFilter ? ev.type_event === typeFilter : true;

      // Titre
      const okTitle = q.length === 0 || String(ev.titre || "").toLowerCase().includes(q);

      // Seulement avec inscrits (on se base sur countsMap si connu)
      const count = countsMap[ev.id];
      const okHas = !onlyWithRegistrations || (typeof count === "number" ? count > 0 : true);

      return okDate && okCate && okType && okTitle && okHas;
    });
  }, [events, searchEvent, dateFilter, categoryFilter, typeFilter, onlyWithRegistrations, countsMap]);

  // Limitation par page
  const visibleEvents = filteredEvents.slice(0, page * PAGE_SIZE);
  const hasMore = filteredEvents.length > visibleEvents.length;

  const handleCountUpdate = (eventId, count) => {
    setCountsMap((m) => (m[eventId] === count ? m : { ...m, [eventId]: count }));
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Titre page */}
        <HeaderTitle
          role="Enseignant"
          title="Suivi des compétitions"
          align="center"
          className="mb-1"
        />

        {/* Navigation secondaire */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={role} />
        </div>

        {/* Carte filtres + liste */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">

          {/* Bandeau dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 text-white">
            <h2 className="text-lg sm:text-xl font-semibold text-center">
              Gestion des inscriptions & résultats sur les compétitions & événements 🥋
            </h2>
            <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
              Les inscrits d’un événement sont chargés uniquement quand vous dépliez la carte.
            </p>
          </div>

          {/* Filtres */}
          <FiltersBar
            cateEvents={cateEvents}
            eventTypes={eventTypes}
            searchEvent={searchEvent}
            setSearchEvent={setSearchEvent}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            onlyWithRegistrations={onlyWithRegistrations}
            setOnlyWithRegistrations={setOnlyWithRegistrations}
            countsMap={countsMap}
          />

          {/* Liste d’événements */}
          <div className="px-5 pb-6 sm:px-8">
            {loading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse h-24 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40" />
                ))}
              </div>
            ) : visibleEvents.length === 0 ? (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Aucun événement ne correspond à vos filtres.
              </div>
            ) : (
              <div className="space-y-4">
                {visibleEvents.map((ev) => (
                  <EventCard
                    key={ev.id}
                    event={ev}
                    enseignants={enseignants}
                    onCountUpdate={handleCountUpdate}
                  />
                ))}

                {hasMore && (
                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setPage((p) => p + 1)}
                      className="rounded-xl px-4 py-2 ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      Afficher plus
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
};

export default SuiviCompetitions;
